import type { ControlInfo, ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import { constructKeytip } from 'owa-command-ribbon';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from 'owa-mail-ribbon-utils/lib/createMailRibbonButton';
import {
    getDefaultMlrAppButtonStyles,
    getDefaultSlrAppRibbonButtonStyles,
    getDefaultSlrSplitButtonAppRibbonButtonStyles,
} from 'owa-command-ribbon-styles';
import {
    onArchiveClicked,
    onBlockClicked,
    onDeleteClicked,
    onFlagUnflagClicked,
    onForwardClicked,
    onMarkAsReadUnreadClicked,
    onPrintClicked,
    onReplyAllClicked,
    onReplyClicked,
    onReplyWithMeetingClicked,
    onCopilotReplyWithMeetingClicked,
    onReportSplitButtonClicked,
    onShowInImmersiveReaderClicked,
    onMessageRecallClicked,
    onZoomClicked,
    onTrackReadReceiptsClicked,
    onMessageResendClicked,
    onShareToTeamsButtonClicked,
    onChatAroundEmailButtonsClicked,
    onMessageExtensionsFlyoutButtonClicked,
    onClickSweep,
    onPinUnpinClicked,
    onIgnoreStopIgnoreClicked,
} from 'owa-mail-ribbon-command-actions/lib/popoutCommandActions';
import { onAssignPolicyMenuClicked } from '../utils/deferredOnAssignPolicyMenuClicked';
import { onBlockMenuClicked } from '../utils/deferredOnBlockMenuClicked';
import { onCategorizeMenuClicked } from '../utils/deferredOnCategorizeMenuClicked';
import { onFlagUnflagMenuClicked } from '../utils/deferredOnFlagUnflagMenuClicked';
import { onForwardMenuClicked } from '../utils/deferredOnForwardMenuClicked';
import { onReportMenuClicked } from '../utils/deferredOnReportMenuClicked';
import { onRespondMenuClicked } from '../utils/deferredOnRespondMenuClicked';
import { onRulesMenuClicked } from '../utils/deferredOnRulesMenuClicked';
import { onCopilotReplyWithMeetingMenuClicked } from '../utils/deferredOnCopilotReplyWithMeetingMenuClicked';
import { onChatAroundEmailMenuClicked } from '../utils/deferredOnChatAroundEmailMenuClicked';
import { resetMenuDefinitionMutator } from '../../mutatorActions/menuDefinitionMutatorActions';
import { type MailRibbonControlId, type MailRibbonTabId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { type MenuItemType } from 'owa-filterable-menu/lib/components/MenuItemType';
import type { AppButtonOnExecuteParameter, RibbonControlProps } from 'owa-acui';
import { owaComputedFn } from 'owa-computed-fn';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import { getTabIdFromTargetWindow } from 'owa-mail-reading-pane-store/lib/utils/getTabIdFromTargetWindow';
import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import { getComputedCallback } from 'owa-ribbon-mobx-utils/lib/getComputedCallback';
import { getIsPopoutButtonDisabled } from 'owa-mail-ribbon-utils/lib/getIsPopoutButtonDisabled';
import { getIsReadButtonDisabled } from 'owa-mail-ribbon-utils/lib/getIsReadButtonDisabled';
import { getMailMenuItemShouldShow } from 'owa-mail-filterable-menu-behavior';
import { getMenuDefinition } from '../../store/menuDefinitionStore';
import { getSharedControlInfo } from 'owa-mail-ribbon-utils/lib/getSharedControlInfo';
import { getReadRibbonId } from 'owa-mail-ribbon-utils/lib/getReadRibbonId';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import isReplyAllTheDefaultResponse from 'owa-mail-reading-pane-store/lib/utils/isReplyAllTheDefaultResponse';
import { resetCategorizeSearch } from 'owa-mail-ribbon-command-actions/lib/onCategorizeCommandActions';
import { retrieveWindowForRibbon } from 'owa-mail-compose-controls/lib/utils/retrieveEditingInfoForRibbon';
import { onCopyToMenuClicked } from '../utils/deferredOnCopyToMenuClicked';
import { resetMoveToSearch } from 'owa-mail-ribbon-command-actions/lib/resetMoveToSearch';
import { createMoveControl } from '../../util/createMoveControl';
import { type ActionControlId } from 'owa-ribbon-ids/lib/actionControlId';
import isAddinMultiAccountEnabled from 'owa-feature-flags/lib/utils/isAddinMultiAccountEnabled';
import { onSnoozeMenuClicked } from '../utils/deferredOnSnoozeMenuClicked';
import { onDeleteMenuClicked } from '../utils/deferredOnDeleteMenuClicked';
import { isOffline } from 'owa-offline';
import { getTeamsIntegrationViewState } from 'owa-mail-reading-pane-store/lib/utils/getTeamsIntegrationViewState';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import type { ClientMessage } from 'owa-mail-store';
import { onQuickStepsMenuClicked } from '../utils/deferredOnQuickStepsMenuClicked';
import { lazyClearPreCheckedCategories } from 'owa-mail-ribbon-sections/lib/sections';
import {
    areSelectedRowKeysFromSameMailboxInfoIfUnifiedInboxOrMultiAccountSearch,
    getRowKeyMailboxInfoIfUnifiedInboxOrMultiAccountSearch,
    getSelectedTableView,
    isMultiAccountTable,
    isMultipleSelectedItemParts,
} from 'owa-mail-list-store';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { isReportingEnabledForMailbox } from 'owa-mail-ribbon-utils/lib/shouldShowRibbonControl';
import { getIsDisabledForDecryption } from '../../utils/getIsDisabledForDecryption';
import { getSelectedTeamsActionContext } from 'owa-mail-ribbon-command-actions/lib/getSelectedTeamsActionContext';
import {
    getReplyAllByIMDisplayName,
    getReplyByIMDisplayName,
} from 'owa-teams-integration/lib/utils/buildDisplayName';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import { isMessageFileMailboxInfo } from 'owa-client-types/lib/MessageFileMailboxInfo';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

/**
 * This file is components that are shared between the read and read projection ribbons.
 *
 * For controls that are only in the main read ribbon, used readSharedControlDefinitions.
 * For controls that show up in compose-only scenarios, please use composeSharedControlDefinitions.ts.
 */

export const getArchiveControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getArchiveControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo: ControlInfo = getSharedControlInfo(505, props);
    const { projectionTabId, itemId } = props;
    return createMailAppButton(
        getReadRibbonId(505, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            505,
            onArchiveClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            itemId
        ),
        constructKeytip([keytipTabId], 505),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getAssignPolicyControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getAssignPolicyControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(616, props);
    const storeName = 'AssignPolicyMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        getReadRibbonId(616, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 616, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            onExecute: () => {
                onAssignPolicyMenuClicked(keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getBlockControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getBlockControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(658, props);
    const { projectionTabId, itemId } = props;
    const storeName = 'BlockMenuDefinitionStore';
    return createMailAppSplitButton(
        getReadRibbonId(658, itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(658, onBlockClicked, projectionTabId, retrieveWindowForRibbon, itemId),
        constructKeytip([keytipTabId], 658, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        itemId,
        {
            onMenuButtonExecute: () => {
                onBlockMenuClicked(keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getCategorizeControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getCategorizeControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(509, props);
    const storeName = 'CategorizeMenuDefinitionStore';

    return createMailAppFlyoutAnchor(
        getReadRibbonId(509, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 509, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            delayUpdateFocusOnHover: true,
            onExecute: () => {
                onCategorizeMenuClicked(keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetCategorizeSearch(undefined /* showAllCategories */);
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
                lazyClearPreCheckedCategories.import().then(clear => clear(props.projectionTabId));
            },
            shouldFocusOnMount: false, // To not override searchbox focus
            shouldFocusOnContainer: false, // To not override searchbox focus
            enableDownArrowNavigation: true, // to allow the searchbox lose its focus on the menu and enable the down arrow key navigation
        }
    );
});

export const getDeleteControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getDeleteControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(519, props);
    const { projectionTabId, itemId } = props;
    return createMailAppButton(
        getReadRibbonId(519, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(519, onDeleteClicked, projectionTabId, retrieveWindowForRibbon, itemId),
        constructKeytip([keytipTabId], 519, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getFlagUnflagControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getFlagUnflagControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(527, props);
    const storeName = 'FlagUnflagMenuDefinitionStore';
    const overrides = {
        onMenuButtonExecute: () => {
            onFlagUnflagMenuClicked(keytipTabId, props);
        },
        onAfterMenuDismiss: () => {
            resetMenuDefinitionMutator(storeName, props.projectionTabId);
        },
    };
    return createMailAppSplitButton(
        getReadRibbonId(527, props.itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(
            527,
            onFlagUnflagClicked,
            undefined /* flagType */,
            undefined /* override */,
            props.itemId
        ),
        constructKeytip([keytipTabId], 527, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        props.itemId,
        overrides
    );
});

export const getForwardControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getForwardControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(555, props);
    const storeName = 'ForwardMenuDefinitionStore';
    const { projectionTabId, itemId } = props;
    return createMailAppSplitButton(
        getReadRibbonId(555, itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(
            555,
            onForwardClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            itemId
        ),
        constructKeytip([keytipTabId], 555),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        itemId,
        {
            onMenuButtonExecute: () => {
                onForwardMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getImmersiveReaderControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getImmersiveReaderControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(532, props);
    const { projectionTabId, itemId } = props;
    return createMailAppButton(
        getReadRibbonId(532, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            532,
            onShowInImmersiveReaderClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            itemId
        ),
        constructKeytip([keytipTabId], 532),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getPrintControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getPrintControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { projectionTabId, itemId, isPopout } = props;
    const composeViewState = getComposeViewState();
    const tabId = projectionTabId || getTabIdFromTargetWindow();
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const isPrintInProgress = readingPaneStore().itemPrintPaneViewStates.get(tabId)?.isPrint;
    const isDisabled =
        isPrintInProgress ||
        isMultipleSelectedItemParts() ||
        (isPopout
            ? getIsPopoutButtonDisabled(549, itemId)
            : getIsReadButtonDisabled(549, false /*isPopout*/, mailboxInfo, undefined /*itemId*/) ||
              composeViewState?.isComposeRendered); /* disable if the button should be disabled, or if we're composing, or if print item is in progress */
    const controlInfo = getSharedControlInfo(549, props);
    return createMailAppButton(
        getReadRibbonId(549, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(549, onPrintClicked, projectionTabId, retrieveWindowForRibbon, itemId),
        constructKeytip([keytipTabId], 549),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /*itemId*/,
        { disabled: isDisabled }
    );
});

export const getReplyControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getReplyControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(556, props);
    const { projectionTabId, itemId } = props;
    return createMailAppButton(
        getReadRibbonId(556, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(556, onReplyClicked, projectionTabId, retrieveWindowForRibbon, itemId),
        constructKeytip([keytipTabId], 556),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getReplyAllControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getReplyAllControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(557, props);
    const { projectionTabId, itemId } = props;
    return createMailAppButton(
        getReadRibbonId(557, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            557,
            onReplyAllClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            itemId
        ),
        constructKeytip([keytipTabId], 557),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getReplyWithMeetingControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getReplyWithMeetingControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(553, props);
    return createMailAppButton(
        getReadRibbonId(553, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(553, onReplyWithMeetingClicked, mailboxInfo, props.itemId),
        constructKeytip([keytipTabId], 553),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getCopilotReplyWithMeetingFlyoutControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getCopilotReplyWithMeetingFlyoutControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(698, props);
    const { itemId, projectionTabId } = props;

    const storeName = 'CopilotMeetingMenuDefinitionStore';
    return createMailAppSplitButton(
        getReadRibbonId(698, itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(
            698,
            onCopilotReplyWithMeetingClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            mailboxInfo,
            itemId
        ),
        constructKeytip([keytipTabId], 698, true /* hasMenu */),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        itemId,
        {
            onMenuButtonExecute: () => {
                onCopilotReplyWithMeetingMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getCopilotReplyWithMeetingControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getCopilotReplyWithMeetingControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(696, props);
    return createMailAppButton(
        getReadRibbonId(696, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            696,
            onCopilotReplyWithMeetingClicked,
            props.projectionTabId,
            retrieveWindowForRibbon,
            mailboxInfo,
            props.itemId
        ),
        constructKeytip([keytipTabId], 696),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getReportControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getReportControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(657, props);
    const { itemId } = props;
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const tableView = getSelectedTableView();
    const isCrossMailboxTable =
        isUnifiedInboxMailboxInfo(mailboxInfo) || isMultiAccountTable(tableView);

    // We override: enable when all selected items are from the same mailbox
    // AND that mailbox supports native reporting, disable otherwise.
    const isSameMailboxInCrossMailboxTable =
        isCrossMailboxTable &&
        areSelectedRowKeysFromSameMailboxInfoIfUnifiedInboxOrMultiAccountSearch(
            mailboxInfo,
            tableView
        );

    // When items are from the same mailbox, check if that specific mailbox supports reporting
    let shouldDisableInCrossMailboxTable = !isSameMailboxInCrossMailboxTable;
    if (isCrossMailboxTable && isSameMailboxInCrossMailboxTable) {
        const firstRowKey = [...tableView.selectedRowKeys.keys()][0];
        if (firstRowKey) {
            const selectedMailboxInfo = getRowKeyMailboxInfoIfUnifiedInboxOrMultiAccountSearch(
                mailboxInfo,
                tableView,
                firstRowKey
            );
            shouldDisableInCrossMailboxTable = !isReportingEnabledForMailbox(selectedMailboxInfo);
        }
    }

    const storeName = 'ReportMenuDefinitionStore';
    return createMailAppSplitButton(
        getReadRibbonId(657, itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(657, onReportSplitButtonClicked, props, retrieveWindowForRibbon),
        constructKeytip([keytipTabId], 657, true /* hasMenu */),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        itemId,
        {
            onMenuButtonExecute: () => {
                onReportMenuClicked(keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
            ...(isCrossMailboxTable && {
                disabled: shouldDisableInCrossMailboxTable,
                primaryDisabled: shouldDisableInCrossMailboxTable,
            }),
        }
    );
});

export const getRespondFlyoutControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRespondFlyoutControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const isDefaultReplyAll = isReplyAllTheDefaultResponse();
    const controlInfo = getSharedControlInfo(678, props);
    const { itemId, projectionTabId, isPopout } = props;
    const popoutControlId = isDefaultReplyAll ? 681 : 679;
    const menuItem = isDefaultReplyAll ? 61 : 60;
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const storeName = 'RespondMenuDefinitionStore';

    return createMailAppSplitButton(
        getReadRibbonId(678, itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(
            678,
            isDefaultReplyAll ? onReplyAllClicked : onReplyClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            itemId
        ),
        constructKeytip([keytipTabId], 678),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrSplitButtonAppRibbonButtonStyles() : undefined,
        undefined /*itemId*/,
        {
            primaryDisabled: isPopout
                ? getIsPopoutButtonDisabled(popoutControlId, itemId)
                : !getMailMenuItemShouldShow(menuItem, mailboxInfo),
            onMenuButtonExecute: () => {
                onRespondMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getSweepControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getSweepControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(572, props);
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    return createMailAppButton(
        getReadRibbonId(572, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            572,
            onClickSweep,
            props.projectionTabId,
            retrieveWindowForRibbon,
            mailboxInfo,
            props.isPopout ? props?.itemId?.Id : undefined
        ),
        constructKeytip([keytipTabId], 572),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getMoveControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getMoveControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    return createMoveControl(keytipTabId, props, {
        isSLR,
        includeItemId: true,
    });
});

export const getRulesControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getRulesControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(559, props);
    const storeName = 'RulesMenuDefinitionStore';
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    return createMailAppFlyoutAnchor(
        getReadRibbonId(559, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 559, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            onExecute: () => {
                onRulesMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getUnreadReadControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getUnreadReadControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(552, props);
    return createMailAppButton(
        getReadRibbonId(552, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(552, onMarkAsReadUnreadClicked, props.itemId),
        constructKeytip([keytipTabId], 552),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getMessageRecallControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getMessageRecallControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { projectionTabId, itemId } = props;
    const controlInfo = getSharedControlInfo(686, props);
    return createMailAppButton(
        getReadRibbonId(686, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            686,
            onMessageRecallClicked,
            itemId,
            getRibbonMailboxInfo(props.mailboxInfo),
            projectionTabId,
            retrieveWindowForRibbon
        ),
        constructKeytip([keytipTabId], 686),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getZoomControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getZoomControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { projectionTabId } = props;
    const controlInfo = getSharedControlInfo(688, props);
    return createMailAppButton(
        688,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            keytipTabId,
            onZoomClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            props.mailboxInfo
        ),
        constructKeytip([keytipTabId], 688),
        controlInfo.iconName,
        undefined,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined
    );
});

export const getTrackReadReceiptsControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getTrackReadReceiptsControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { projectionTabId, itemId } = props;
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(697, props);
    return createMailAppButton(
        getReadRibbonId(697, itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            697,
            onTrackReadReceiptsClicked,
            mailboxInfo,
            itemId,
            projectionTabId,
            retrieveWindowForRibbon
        ),
        constructKeytip([keytipTabId], 697),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        itemId
    );
});

export const getChatAroundEmailControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getChatAroundEmailControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);

    const controlInfo = getSharedControlInfo(731, props);
    const storeName = 'TeamsChatMenuDefinitionStore';

    return createMailAppFlyoutAnchor(
        getReadRibbonId(731, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 731, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            onExecute: () => {
                onChatAroundEmailMenuClicked(isSLR, keytipTabId, props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getChatAroundEmailSenderControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getChatAroundEmailSenderControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    return getChatAroundEmailButtonControl(true /* senderOnly */, isSLR, keytipTabId, props);
});

export const getChatAroundEmailAllControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getChatAroundEmailAllControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    return getChatAroundEmailButtonControl(false /* senderOnly */, isSLR, keytipTabId, props);
});

function getChatAroundEmailButtonControl(
    senderOnly: boolean,
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { item, senderName } = getSelectedTeamsActionContext(props.itemId);
    const message = item as ClientMessage | undefined;
    const controlId = senderOnly ? 702 : 703;
    const controlInfo = getSharedControlInfo(controlId, props);
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const teamsIntegrationViewState =
        isMessageFileMailboxInfo(message?.MailboxInfo) &&
        isGlobalFeatureEnabled('mon-msg-local-parser')
            ? getItemReadingPaneViewState(message?.ItemId?.Id)?.itemViewState
                  ?.teamsIntegrationViewState
            : getTeamsIntegrationViewState(message);
    const isEnabled = senderOnly
        ? teamsIntegrationViewState?.isChatAroundEmailValid
        : teamsIntegrationViewState?.isChatAroundEmailAllValid;
    const label = senderOnly
        ? getReplyByIMDisplayName(senderName.split(' ')[0])
        : getReplyAllByIMDisplayName(message?.ItemClass);

    return createMailAppButton(
        getReadRibbonId(controlId, props.itemId),
        label,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            controlId,
            onChatAroundEmailButtonsClicked,
            isSLR,
            props.itemId,
            mailboxInfo,
            senderOnly
        ),
        constructKeytip([keytipTabId], controlId),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            disabled:
                !isEnabled ||
                getIsReadButtonDisabled(controlId, props.isPopout, mailboxInfo, props.itemId),
        }
    );
}

export const getShareToTeamsChatControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps | undefined = owaComputedFn(function getShareToTeamsChatControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps | undefined {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(709, props);

    const itemIdForViewStateLookup =
        props.itemId?.Id ??
        (props.itemId ? getMailStore().items.get(props.itemId.Id)?.ItemId?.Id : undefined);

    const teamsIntegrationViewState = getTeamsIntegrationViewState(
        itemIdForViewStateLookup ? getMailStore().items.get(itemIdForViewStateLookup) : undefined
    );

    const isShareToTeamsValid = teamsIntegrationViewState?.isShareToTeamsValid;

    const isShareToTeamsEnabled =
        isShareToTeamsValid &&
        !getIsReadButtonDisabled(709, props.isPopout, mailboxInfo, props.itemId);

    return createMailAppButton(
        709,
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(709, onShareToTeamsButtonClicked, props.itemId, mailboxInfo),
        constructKeytip([keytipTabId], 709),
        controlInfo.iconName,
        undefined /* iconColor */,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : getDefaultMlrAppButtonStyles(),
        props.itemId,
        {
            disabled: !isShareToTeamsEnabled,
        }
    );
});

export const getCopyToControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getCopyToControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo: ControlInfo = getSharedControlInfo(705, props);
    const storeName = 'CopyToMenuDefinitionStore';

    return createMailAppFlyoutAnchor(
        getReadRibbonId(705, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 705, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            delayUpdateFocusOnHover: true,
            onExecute: () => {
                resetMoveToSearch(true /* onEnter */, props.projectionTabId);
                onCopyToMenuClicked(keytipTabId, props, mailboxInfo);
            },
            onAfterMenuDismiss: () => {
                resetMoveToSearch(false /* onEnter */, props.projectionTabId);
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
            shouldFocusOnMount: false, // To not override searchbox focus
            shouldFocusOnContainer: false, // To not override searchbox focus
            enableDownArrowNavigation: true, // to allow the searchbox lose its focus on the menu and enable the down arrow key navigation
        }
    );
});

export const getMessageResendControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getMessageResendControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const { projectionTabId, itemId } = props;
    const controlInfo = getSharedControlInfo(712, props);
    return createMailAppButton(
        getReadRibbonId(712, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(
            712,
            onMessageResendClicked,
            itemId,
            projectionTabId,
            retrieveWindowForRibbon
        ),
        constructKeytip([keytipTabId], 712),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getReadMessageExtensionsControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getReadMessageExtensionsControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(7016, props);
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);

    // Check if addins should be disabled for decryption
    const isDisabledForDecryption = getIsDisabledForDecryption(props?.itemId, mailboxInfo);

    return createMailAppButton(
        getReadRibbonId(7016, props?.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        (params?: AppButtonOnExecuteParameter) => {
            if (params) {
                getComputedCallback(
                    7016,
                    onMessageExtensionsFlyoutButtonClicked,
                    params,
                    props?.itemId,
                    props?.projectionTabId,
                    retrieveWindowForRibbon,
                    isAddinMultiAccountEnabled() ? mailboxInfo : undefined
                )();
            }
        },
        constructKeytip([keytipTabId], 7016),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        undefined /*itemId*/,
        {
            disabled:
                getIsReadButtonDisabled(7016, props.isPopout, mailboxInfo, props?.itemId) ||
                isOffline() ||
                isDisabledForDecryption,
        }
    );
});

export const getQuickStepFlyoutControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getQuickStepFlyoutControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(550, props);
    const storeName = 'QuickStepsMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        getReadRibbonId(550, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 550, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            onExecute: () => {
                onQuickStepsMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getPinUnpinControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getPinUnpinControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(548, props);
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    return createMailAppButton(
        getReadRibbonId(548, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(548, onPinUnpinClicked, props.itemId, mailboxInfo),
        constructKeytip([keytipTabId], 548),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId
    );
});

export const getSnoozeControl: (
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getSnoozeControl(
    isSLR: boolean,
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    const controlInfo = getSharedControlInfo(571, props);
    const storeName = 'SnoozeMenuDefinitionStore';
    return createMailAppFlyoutAnchor(
        getReadRibbonId(571, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        mailboxInfo,
        constructKeytip([keytipTabId], 571, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
        props.itemId,
        {
            onExecute: () => {
                onSnoozeMenuClicked(props);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});

export const getIgnoreControl: (
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getIgnoreControl(
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo: ControlInfo = getSharedControlInfo(531, props);
    return createMailAppButton(
        getReadRibbonId(531, props.itemId),
        controlInfo.text,
        controlInfo.tooltip,
        props,
        getComputedCallback(531, onIgnoreStopIgnoreClicked, props, retrieveWindowForRibbon),
        constructKeytip([keytipTabId], 531),
        controlInfo.iconName,
        undefined /*iconColor*/,
        undefined /*styles*/,
        props.itemId
    );
});

export const getDeleteMenuControl: (
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
) => RibbonControlProps = owaComputedFn(function getDeleteMenuControl(
    keytipTabId: MailRibbonTabId,
    props: ReadOnlyRibbonControlDefProps
): RibbonControlProps {
    const controlInfo = getSharedControlInfo(519, props);
    const { projectionTabId } = props;
    const storeName = 'DeleteMenuDefinitionStore';
    return createMailAppSplitButton(
        getReadRibbonId(519, props.itemId),
        controlInfo.text,
        controlInfo.ariaText,
        controlInfo.tooltip,
        props,
        getMenuDefinition(storeName, props.projectionTabId),
        getComputedCallback(
            519,
            onDeleteClicked,
            projectionTabId,
            retrieveWindowForRibbon,
            props.itemId /* itemId */
        ),
        constructKeytip([keytipTabId], 519, true /* hasMenu */),
        controlInfo.iconName,
        undefined /*iconColor*/,
        getDefaultSlrSplitButtonAppRibbonButtonStyles(),
        props.itemId /*itemId*/,
        {
            onMenuButtonExecute: () => {
                onDeleteMenuClicked(props, keytipTabId);
            },
            onAfterMenuDismiss: () => {
                resetMenuDefinitionMutator(storeName, props.projectionTabId);
            },
        }
    );
});
